import { useEffect, useState } from 'react'
import { api, type ExcipientRequirementOption, type Product } from '../lib/api.ts'
import { answerChange, withAnswer } from '../lib/excipientAnswers.ts'
import { Button } from './Button.tsx'
import { Checkbox } from './Checkbox.tsx'
import { useToast } from './Toast.tsx'

/**
 * Which actives this product is the right excipient for, at this pharmacy
 * (#617, ADR 0043).
 *
 * `excipient-mismatched-to-active` compares it with the actives' class and
 * hygroscopicity, shown just above on the same page. The ERP records nothing
 * of the kind, so a product nobody ticked is one the rule never judges.
 */
export function ExcipientIndication({
  accountId,
  product,
  requirements,
  onChanged,
}: {
  accountId: string | undefined
  product: Product
  requirements: readonly ExcipientRequirementOption[]
  onChanged: () => void
}) {
  const saved = product.excipientAnswers?.answers ?? []
  const [chosen, setChosen] = useState<string[]>([...saved])
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const change = answerChange(saved, chosen)

  // A reload brings the stored answers; the boxes follow them.
  useEffect(() => {
    setChosen([...(product.excipientAnswers?.answers ?? [])])
  }, [product.excipientAnswers])

  async function save() {
    if (accountId === undefined || change === 'none') return
    setBusy(true)
    try {
      if (change === 'clear') await api.clearExcipientAnswers(accountId, product.erpCode)
      else await api.setExcipientAnswers(accountId, product.erpCode, chosen)
      toast.show(
        change === 'clear'
          ? `${product.name} deixa de ser avaliado como excipiente`
          : `Indicação de ${product.name} salva`,
        { tone: 'success' },
      )
      onChanged()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const origin = product.excipientAnswers
  return (
    <div className="flex flex-col items-start gap-2">
      {requirements.map((one) => (
        <Checkbox
          key={one.id}
          label={one.label}
          checked={chosen.includes(one.id)}
          disabled={busy}
          onChange={(event) =>
            setChosen((current) => withAnswer(current, one.id, event.target.checked, requirements))
          }
        />
      ))}
      {origin !== null && (
        <p className="text-tSmall text-text-weak">
          {`Definido por ${origin.by} em ${new Date(origin.at).toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
          })}`}
        </p>
      )}
      <Button
        size="small"
        variant="secondary"
        tone="brand"
        disabled={busy || change === 'none' || accountId === undefined}
        onClick={() => void save()}
      >
        {busy ? 'Salvando…' : 'Salvar indicação'}
      </Button>
    </div>
  )
}
